/**
 * DiscordFeedWidget — Discord post feed with inline graphics + team logos
 * - Collapsible sections for each post
 * - Full-scroll images when expanded (no height clipping)
 * - Team logos parsed from matchup text
 * Refresh: 60s | CSS prefix: dfw-
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { fetchFromProxy, imageUrl } from '../lib/api'
import { parseMatchup, findTeamsInText, teamLogo } from '../lib/teamLogos'
import { MessageSquare, RefreshCw, Image, FileText, BarChart3, AlertCircle, ChevronRight } from 'lucide-react'
import './DiscordFeedWidget.css'

const CHANNEL_COLORS = {
  '#mlb-schedule': '#1E90FF',
  '#starting-lineups': '#E67E22',
  '#probable-pitchers': '#9B59B6',
  '#umpires': '#2ECC71',
  '#mlb-moneylines': '#00FF00',
  '#mlb-spreads': '#00BFFF',
  '#mlb-totals': '#FFD700',
}

const CHANNEL_LABELS = {
  '#mlb-schedule': 'Schedule',
  '#starting-lineups': 'Lineups',
  '#probable-pitchers': 'Pitchers',
  '#umpires': 'Umpires',
  '#mlb-moneylines': 'Moneylines',
  '#mlb-spreads': 'Spreads',
  '#mlb-totals': 'Totals',
}

const TYPE_ICONS = {
  'text_embed': FileText,
  'graphic': Image,
  'chart': BarChart3,
  'alert': AlertCircle,
}

function timeAgo(isoStr) {
  const diff = Date.now() - new Date(isoStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

/** Inline team logo component */
function TeamLogo({ abbr, size = 20 }) {
  const src = teamLogo(abbr)
  if (!src) return null
  return (
    <img
      src={src}
      alt={abbr}
      className="dfw-team-logo"
      style={{ width: size, height: size }}
      loading="lazy"
      onError={(e) => { e.target.style.display = 'none' }}
    />
  )
}

/** Matchup header with team logos */
function MatchupHeader({ text }) {
  const matchup = parseMatchup(text)
  if (!matchup) return null

  return (
    <div className="dfw-matchup">
      {matchup.away && (
        <div className="dfw-matchup-team">
          <img src={matchup.away.logo} alt={matchup.away.abbr} className="dfw-team-logo-lg" loading="lazy" onError={(e) => { e.target.style.display = 'none' }} />
          <span className="dfw-team-name">{matchup.away.short || matchup.awayText}</span>
          <span className="dfw-team-abbr">{matchup.away.abbr}</span>
        </div>
      )}
      {!matchup.away && (
        <div className="dfw-matchup-team">
          <span className="dfw-team-name">{matchup.awayText}</span>
        </div>
      )}
      <span className="dfw-matchup-at">@</span>
      {matchup.home && (
        <div className="dfw-matchup-team">
          <img src={matchup.home.logo} alt={matchup.home.abbr} className="dfw-team-logo-lg" loading="lazy" onError={(e) => { e.target.style.display = 'none' }} />
          <span className="dfw-team-name">{matchup.home.short || matchup.homeText}</span>
          <span className="dfw-team-abbr">{matchup.home.abbr}</span>
        </div>
      )}
      {!matchup.home && (
        <div className="dfw-matchup-team">
          <span className="dfw-team-name">{matchup.homeText}</span>
        </div>
      )}
    </div>
  )
}

/** Single collapsible post card */
function PostCard({ post, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  const contentRef = useRef(null)
  const TypeIcon = TYPE_ICONS[post.post_type] || FileText
  const chColor = CHANNEL_COLORS[post.channel] || '#888'
  const imgSrc = post.image_url ? imageUrl(post.image_url) : null

  // Parse matchup from content or title
  const matchupText = post.content || post.title || ''
  const hasMatchup = parseMatchup(matchupText) !== null

  // Find teams mentioned in description for logo enrichment
  const descTeams = findTeamsInText(post.description || '')

  return (
    <div className={`dfw-post ${open ? 'open' : ''}`}>
      {/* Collapsible header — always visible */}
      <div className="dfw-post-header" onClick={() => setOpen(!open)}>
        <ChevronRight size={14} className={`dfw-chevron ${open ? 'rotated' : ''}`} />
        <span className="dfw-channel-badge" style={{ borderColor: chColor, color: chColor }}>
          {CHANNEL_LABELS[post.channel] || post.channel}
        </span>
        <span className="dfw-post-type"><TypeIcon size={12} /></span>

        {/* Matchup logos in header when collapsed */}
        {hasMatchup && <MatchupHeader text={matchupText} />}

        {/* Title (only if no matchup parsed, to avoid duplication) */}
        {!hasMatchup && post.title && (
          <span className="dfw-header-title" style={{ color: post.color ? `#${post.color.toString(16).padStart(6, '0')}` : chColor }}>
            {post.title}
          </span>
        )}

        {post.has_image && <Image size={12} className="dfw-has-image-icon" />}
        <span className="dfw-time">{timeAgo(post.timestamp)}</span>
      </div>

      {/* Collapsible body */}
      {open && (
        <div className="dfw-post-body" ref={contentRef}>
          {/* Title with color (if matchup was shown in header, show full title here too) */}
          {post.title && (
            <div className="dfw-post-title" style={{ color: post.color ? `#${post.color.toString(16).padStart(6, '0')}` : chColor }}>
              {post.title}
            </div>
          )}

          {/* Content text with team logos inline */}
          {post.content && (
            <div className="dfw-post-content">{post.content}</div>
          )}

          {/* Description */}
          {post.description && (
            <div className="dfw-post-description">
              {/* Show team logos found in description */}
              {descTeams.length > 0 && (
                <div className="dfw-desc-teams">
                  {descTeams.map(t => (
                    <img key={t.abbr} src={t.logo} alt={t.abbr} className="dfw-team-logo-sm" title={t.name} loading="lazy" onError={(e) => { e.target.style.display = 'none' }} />
                  ))}
                </div>
              )}
              {post.description}
            </div>
          )}

          {/* Embed fields */}
          {post.fields && post.fields.length > 0 && (
            <div className="dfw-post-fields">
              {post.fields.map((f, fi) => (
                <div key={fi} className={`dfw-field ${f.inline ? 'inline' : ''}`}>
                  <span className="dfw-field-name">{f.name}</span>
                  <span className="dfw-field-value">{f.value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Full-size image — no height restriction, scrollable */}
          {imgSrc && (
            <div className="dfw-image-container">
              <img
                src={imgSrc}
                alt={post.image_name || 'Discord attachment'}
                className="dfw-image"
                loading="lazy"
                onError={(e) => { e.target.style.display = 'none' }}
              />
            </div>
          )}

          {/* Fallback image name tag */}
          {!imgSrc && post.image_name && (
            <div className="dfw-image-tag">
              <Image size={10} /> {post.image_name}
            </div>
          )}

          {post.footer && (
            <div className="dfw-post-footer-text">{post.footer}</div>
          )}
        </div>
      )}
    </div>
  )
}


function DiscordFeedWidget({ channelFilter = null, channelFilters = null, showFilterBar = true }) {
  const [posts, setPosts] = useState([])
  const [status, setStatus] = useState('unknown')
  const [lastCheck, setLastCheck] = useState(null)
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState(channelFilter || 'all')
  const [expanded, setExpanded] = useState(true)

  // Multi-channel mode: fetch all, filter client-side
  const multiChannels = channelFilters || null

  const fetchData = useCallback(async () => {
    setLoading(true)

    if (multiChannels) {
      // Fetch all channels in parallel, merge results
      const promises = multiChannels.map(ch =>
        fetchFromProxy('/posts', { channel: ch, limit: '50' })
      )
      const results = await Promise.all(promises)
      let allPosts = []
      let anyOk = false
      for (const res of results) {
        if (res.status === 'ok' && res.data) {
          anyOk = true
          const p = res.data.posts || (Array.isArray(res.data) ? res.data : [])
          allPosts = allPosts.concat(p)
        }
      }
      // Sort by timestamp descending
      allPosts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      // Apply sub-filter if not 'all'
      if (filter !== 'all') {
        allPosts = allPosts.filter(p => p.channel === filter)
      }
      setPosts(allPosts.slice(0, 100))
      setStatus(anyOk ? 'ok' : 'error')
    } else {
      // Single channel mode (original behavior)
      const params = { limit: '100' }
      if (filter !== 'all') {
        params.channel = filter
      }
      const res = await fetchFromProxy('/posts', params)

      if (res.status === 'ok' && res.data) {
        setPosts(res.data.posts || [])
        setStatus('ok')
      } else if (res.status === 'sleeping') {
        setStatus('sleeping')
      } else {
        setStatus('error')
      }
    }
    setLastCheck(new Date())
    setLoading(false)
  }, [filter, multiChannels])

  useEffect(() => {
    fetchData()
    const id = setInterval(fetchData, 60000)
    return () => clearInterval(id)
  }, [fetchData])

  useEffect(() => {
    if (channelFilter) setFilter(channelFilter)
  }, [channelFilter])

  return (
    <div className="widget-card dfw-widget">
      <div className="widget-header dfw-header-clickable" onClick={() => setExpanded(!expanded)}>
        <ChevronRight size={14} className={`dfw-top-chevron ${expanded ? 'rotated' : ''}`} />
        <div className="widget-title">
          <MessageSquare size={16} />
          <span>Discord Feed</span>
        </div>
        <div className="dfw-header-actions">
          <span className={`status-badge status-${status}`}>
            {status === 'ok' ? 'LIVE' : status === 'sleeping' ? 'SLEEPING' : 'OFFLINE'}
          </span>
          <button className="refresh-btn" onClick={(e) => { e.stopPropagation(); fetchData() }} disabled={loading}>
            <RefreshCw size={12} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {expanded && showFilterBar && (
        <div className="dfw-filter-bar">
          <button className={`dfw-filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>ALL</button>
          {(multiChannels
            ? multiChannels.map(ch => [ch, CHANNEL_LABELS[ch] || ch])
            : Object.entries(CHANNEL_LABELS)
          ).map(([ch, label]) => (
            <button key={ch} className={`dfw-filter-btn ${filter === ch ? 'active' : ''}`} onClick={() => setFilter(ch)} style={{ '--ch-color': CHANNEL_COLORS[ch] || '#888' }}>{label}</button>
          ))}
        </div>
      )}

      {expanded && (
        <>
          <div className="widget-body dfw-feed">
            {status === 'sleeping' && <div className="dfw-sleeping">HF Space is sleeping. Posts will appear when monitors are active.</div>}
            {status === 'ok' && posts.length === 0 && <div className="dfw-empty">No posts yet. Posts appear after monitors start posting to Discord.</div>}

            {posts.map((post, i) => (
              <PostCard key={`${post.timestamp}-${i}`} post={post} defaultOpen={i === 0} />
            ))}
          </div>

          <div className="widget-footer">
            <span>{posts.length} posts</span>
            <span>{lastCheck ? `Updated ${lastCheck.toLocaleTimeString()}` : ''}</span>
          </div>
        </>
      )}
    </div>
  )
}

export default DiscordFeedWidget
